"use client";

import { cn } from "@/lib/utils";

const TIMELINE_OPTIONS = [
  { value: "IMMEDIATELY", label: "Now" },
  { value: "WITHIN_3_MONTHS", label: "1-3 mo" },
  { value: "WITHIN_6_MONTHS", label: "3-6 mo" },
  { value: "WITHIN_1_YEAR", label: "6-12 mo" },
  { value: "JUST_BROWSING", label: "Browsing" },
] as const;

const EXPERIENCE_OPTIONS = [
  { value: "FIRST_TIME", label: "First time" },
  { value: "HAVE_OWNED", label: "Have owned" },
  { value: "SERIAL_BUYER", label: "Serial buyer" },
] as const;

type BuyerTimeline = (typeof TIMELINE_OPTIONS)[number]["value"];
type BuyerExperience = (typeof EXPERIENCE_OPTIONS)[number]["value"];

export type BuyerProfileData = {
  buyerTimeline?: BuyerTimeline;
  buyerExperience?: BuyerExperience;
  buyerBudgetMin?: number;
  buyerBudgetMax?: number;
};

function PillGroup<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { value: T; label: string }[];
  value?: T;
  onChange: (val: T | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(value === opt.value ? undefined : opt.value)}
          className={cn(
            "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            value === opt.value
              ? "border-foreground bg-foreground text-background"
              : "border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function BuyerProfileFields({
  data,
  onChange,
}: {
  data: BuyerProfileData;
  onChange: (data: BuyerProfileData) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Help us match you better
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Buying timeline
          </span>
          <PillGroup
            options={TIMELINE_OPTIONS}
            value={data.buyerTimeline}
            onChange={(val) => onChange({ ...data, buyerTimeline: val })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Experience
          </span>
          <PillGroup
            options={EXPERIENCE_OPTIONS}
            value={data.buyerExperience}
            onChange={(val) => onChange({ ...data, buyerExperience: val })}
          />
        </div>
      </div>
    </div>
  );
}

BuyerProfileFields.displayName = "BuyerProfileFields";
